import React, { useEffect, useState } from 'react';
import { useParams, Link, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import termsData from '../../../constants/termsData';
import { getTranslatedTerms } from '../../../constants/termsDataI18n';
import { translateHtml } from '../../../api/deepl';
import { getCurrentBeLang } from '../../../contexts/LanguageContext';

export default function PolicyPage() {
  const { t, i18n } = useTranslation();
  const MENU = [
    { slug: 'service', label: t('auth.policy_service') },
    { slug: 'privacyConsent', label: t('common.privacy_consent') },
    { slug: 'privacy', label: t('auth.policy_privacy') },
    { slug: 'marketing', label: t('auth.policy_marketing') },
    { slug: 'refund', label: t('auth.policy_refund') },
  ];

  const { slug } = useParams();
  const location = useLocation();
  const activeSlug = slug || 'service';
  const koData = termsData[activeSlug];
  const beLang = i18n.language || 'ko';

  // 1순위: 정적 사전 번역(termsDataI18n) / 2순위: DeepL 런타임 / 3순위: 한국어 원문
  const pretranslated = getTranslatedTerms(activeSlug, beLang);
  const titleToShow = pretranslated?.title || koData?.title;
  const [translatedHtml, setTranslatedHtml] = useState(pretranslated?.html || koData?.html);

  useEffect(() => {
    // 언어·페이지 전환 시 즉시 갱신 (정적 번역 우선)
    if (pretranslated?.html) {
      setTranslatedHtml(pretranslated.html);
      return;
    }
    setTranslatedHtml(koData?.html);
    if (!koData?.html) return;
    const bl = getCurrentBeLang();
    if (bl === 'ko') return;
    let cancelled = false;
    translateHtml(koData.html, bl).then((out) => {
      if (!cancelled) setTranslatedHtml(out);
    });
    return () => { cancelled = true; };
    // pretranslated는 slug·beLang 변화에 따라 재계산되므로 둘을 dep로 둠
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeSlug, beLang]);

  return (
    <div className="bg-white min-h-screen">
      <div className="max-w-[1400px] mx-auto px-2.5 sm:px-5 lg:px-10 py-8 sm:py-12">
        <div className="flex flex-col lg:flex-row gap-6 lg:gap-0">
          {/* Mobile: Tab navigation with underline */}
          <div className="lg:hidden border-b border-mono-100">
            <div className="flex overflow-x-auto scrollbar-hide">
              {MENU.map((item) => (
                <Link
                  key={item.slug}
                  to={`/policy/${item.slug}`}
                  className={`whitespace-nowrap px-3 py-3 text-2sm font-medium relative shrink-0 transition ${
                    activeSlug === item.slug
                      ? 'text-mono-900'
                      : 'text-mono-400 hover:text-mono-700'
                  }`}
                >
                  {item.label}
                  {activeSlug === item.slug && (
                    <span className="absolute bottom-0 left-0 right-0 h-0.5 bg-orange-500" />
                  )}
                </Link>
              ))}
            </div>
          </div>

          {/* Desktop: Sidebar — 다국어(영문 등) 긴 라벨 wrap 허용. break-keep으로 단어 중간 끊김 방지. */}
          <nav className="hidden lg:block lg:w-[300px] shrink-0 lg:pr-8">
            <div className="flex flex-col gap-1 lg:sticky lg:top-24">
              {MENU.map((item) => (
                <Link
                  key={item.slug}
                  to={`/policy/${item.slug}`}
                  className={`break-keep px-3 py-2.5 rounded-lg text-sm font-medium leading-snug transition ${
                    activeSlug === item.slug
                      ? 'bg-mono-900 text-white'
                      : 'text-mono-500 hover:bg-mono-50 hover:text-mono-900'
                  }`}
                >
                  {item.label}
                </Link>
              ))}
            </div>
          </nav>

          {/* Content */}
          <div className="flex-1 min-w-0 lg:pl-12 lg:border-l lg:border-mono-100">
            <h1 className="text-xl sm:text-2xl font-bold text-mono-900 mb-8 break-keep">{titleToShow || t('auth.policy_terms')}</h1>
            {koData ? (
              <div
                className="policy-content text-sm text-mono-700 leading-relaxed break-keep"
                dangerouslySetInnerHTML={{ __html: translatedHtml || koData.html }}
              />
            ) : (
              <p className="text-sm text-mono-400 py-16 text-center">{t('auth.policy_not_found')}</p>
            )}
          </div>
        </div>
      </div>

      {/* Policy content styles */}
      <style>{`
        .policy-content h3 {
          font-size: 0.9375rem;
          font-weight: 700;
          color: #0C0F16;
          margin-top: 1.5rem;
          margin-bottom: 0.5rem;
        }
        .policy-content p {
          margin-bottom: 0.5rem;
        }
        .policy-content table {
          width: 100%;
          border-collapse: collapse;
          margin: 1rem 0;
          font-size: 0.8125rem;
        }
        .policy-content th,
        .policy-content td {
          border: 1px solid #E2E5EB;
          padding: 0.625rem 0.75rem;
          text-align: left;
          vertical-align: top;
        }
        .policy-content th {
          background-color: #F8F9FC;
          font-weight: 600;
          color: #2E3442;
        }
        .policy-content td {
          color: #4E5564;
        }
      `}</style>
    </div>
  );
}
